import React, { CSSProperties, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import {
  Button,
  Card,
  Result,
  Select,
  Skeleton,
  Table,
  Typography,
  message,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import {
  GetClientApprovedTemplatesQuery,
  useGetClientApprovedTemplatesQuery,
  useGetClientPracticesForPacketQuery,
} from '../graphql/generated';
import { generateLetterTemplatePDF } from '../components/templates/LetterTemplatePreview';
import { getLanguageLabel } from '../components/utils/LanguageSelect';
import { compareClientNetworks } from './clientNetworkSort';

interface PacketSummaryRow {
  network_name: string;
  practice_name: string;
  tin: string | null | undefined;
  practice_id: string;
  requested_language: string;
  template_name: string;
  status: string;
  short_url: string;
}

const getValidFilename = (value: string) => value.replace(/[\\/:"*?<>|]/g, '-');

const isPacketPractice = (practice: {
  practice_description?: string | null;
  practice_name?: string | null;
}) =>
  !practice.practice_description?.includes('#HEDR') &&
  !practice.practice_description?.includes('#BNL') &&
  !practice.practice_name?.includes('Unaffiliated');

const packetNetworkLabel = (network: {
  dc_id?: string | null;
  network_name: string;
}) =>
  network.dc_id
    ? `${network.network_name} (${network.dc_id})`
    : network.network_name;

async function processInBatches<T>(
  items: T[],
  worker: (item: T) => Promise<void>,
  batchSize: number,
  waitTime: number
): Promise<void> {
  for (let i = 0; i < items.length; i += batchSize) {
    await Promise.all(items.slice(i, i + batchSize).map(worker));
    if (i + batchSize < items.length) {
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }
  }
}

const buildSummaryCsv = (rows: PacketSummaryRow[]) => {
  const escape = (value: string | null | undefined) =>
    `"${(value || '').replace(/"/g, '""')}"`;
  const header =
    'network_name,practice_name,tin,practice_id,requested_language,template_name,status,short_url';
  const lines = rows.map((row) =>
    [
      row.network_name,
      row.practice_name,
      row.tin,
      row.practice_id,
      row.requested_language,
      row.template_name,
      row.status,
      row.short_url,
    ]
      .map(escape)
      .join(',')
  );
  return [header, ...lines].join('\n') + '\n';
};

const pageStyle: CSSProperties = {
  maxWidth: 1120,
  margin: '0 auto',
};

const cardStyle: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
  marginTop: 16,
};

type ApprovedTemplateRow = GetClientApprovedTemplatesQuery['network_templates'][number];
type ApprovedLetterTemplateRow = ApprovedTemplateRow & {
  template_language: string;
  letter_template: NonNullable<ApprovedTemplateRow['letter_template']>;
};

const templateName = (record: ApprovedTemplateRow) =>
  record.letter_template?.letter_template_name ??
  record.email_template?.email_template_name ??
  record.sms_template?.sms_template_name ??
  record.template_id;

const ApprovedTemplatesTable: React.FC<{ rows: ApprovedTemplateRow[] }> = ({
  rows,
}) => (
  <Table
    dataSource={rows}
    rowKey="network_template_id"
    size="small"
    pagination={false}
    columns={[
      {
        title: 'Network',
        render: (_, record: ApprovedTemplateRow) => record.network.network_name,
      },
      { title: 'Type', dataIndex: 'template_type' },
      {
        title: 'Language',
        dataIndex: 'template_language',
        render: getLanguageLabel,
      },
      {
        title: 'Template',
        render: (_, record: ApprovedTemplateRow) => templateName(record),
      },
    ]}
  />
);

const ClientTemplatesPage: React.FC = () => {
  const [pickedNetworkId, setSelectedNetworkId] = useState<string>();
  const [packetProgress, setPacketProgress] = useState<{
    completed: number;
    family: 'SVA' | 'BNL';
    total: number;
  }>();
  const approvedTemplatesQuery = useGetClientApprovedTemplatesQuery();
  const practicesQuery = useGetClientPracticesForPacketQuery();

  const approvedTemplates =
    approvedTemplatesQuery.data?.network_templates || [];
  const networks = practicesQuery.data?.networks || [];
  const selectedNetworkId =
    pickedNetworkId ??
    (networks.length === 1 ? networks[0].network_id : undefined);
  const familyRows = (family: string | null, networkId = selectedNetworkId) =>
    approvedTemplates.filter(
      (t) =>
        t.network_id === networkId && (t.template_family || null) === family
    );

  const downloadPacketRequest = useRequest(
    async (family: 'SVA' | 'BNL', networkId: string) => {
      const network = practicesQuery.data?.networks.find(
        (candidate) => candidate.network_id === networkId
      );
      if (!network) {
        message.error('Please select a network');
        return;
      }

      const letterRows = familyRows(family, networkId).filter(
        (t): t is ApprovedLetterTemplateRow =>
          t.template_type === 'letter' &&
          Boolean(t.template_language) &&
          Boolean(t.letter_template)
      );
      const jszip = new JSZip();
      const folder_name = `${family} Packet Previews ${
        new Date().toISOString().split('T')[0]
      }`;
      const packetFolder = jszip.folder(folder_name);
      const summaryRows: PacketSummaryRow[] = [];

      const networkFolder = packetFolder?.folder(
        getValidFilename(network.network_name)
      );
      const practices = network.practices.filter(isPacketPractice);
      setPacketProgress({ completed: 0, family, total: practices.length });

      await processInBatches(
        practices,
        async (practice) => {
          try {
            const tinSuffix = practice.tin ? `_${practice.tin.slice(-4)}` : '';
            const practiceFolder = networkFolder?.folder(
              getValidFilename(`${practice.practice_name}${tinSuffix}`)
            );
            for (const templateRow of letterRows) {
              const language = templateRow.template_language;
              const template = templateRow.letter_template;
              const summaryRow: PacketSummaryRow = {
                network_name: network.network_name,
                practice_name: practice.practice_name,
                tin: practice.tin,
                practice_id: practice.practice_id,
                requested_language: language,
                template_name: templateName(templateRow),
                status: 'ok',
                short_url: '',
              };
              try {
                const pdf = await generateLetterTemplatePDF(
                  template.external_id,
                  template.external_secondary_id,
                  template.size,
                  template.format,
                  template.letter_template_id,
                  practice.practice_id
                );
                summaryRow.short_url = pdf.mergeVars['signature_url'] || '';
                const fileBase = getValidFilename(
                  `${practice.practice_name}${tinSuffix}_${template.letter_template_name}_${language}`
                );
                if (pdf.back) {
                  practiceFolder?.file(`${fileBase} (Front).pdf`, pdf.front);
                  practiceFolder?.file(`${fileBase} (Back).pdf`, pdf.back);
                } else {
                  practiceFolder?.file(`${fileBase}.pdf`, pdf.front);
                }
              } catch (error: any) {
                summaryRow.status = `error: ${error?.message || error}`;
              }
              summaryRows.push(summaryRow);
            }
          } finally {
            setPacketProgress((current) =>
              current?.family === family
                ? { ...current, completed: current.completed + 1 }
                : current
            );
          }
        },
        25,
        2000
      );

      packetFolder?.file('summary.csv', buildSummaryCsv(summaryRows));
      const blob = await jszip.generateAsync({ type: 'blob' });
      saveAs(blob, `${folder_name}.zip`);
      message.success(`${family} packet previews downloaded`);
    },
    { manual: true }
  );

  if (approvedTemplatesQuery.loading || practicesQuery.loading)
    return <Skeleton />;
  if (approvedTemplatesQuery.error || practicesQuery.error)
    return (
      <Result
        status="error"
        title="There was an error retrieving approved templates"
        subTitle={
          approvedTemplatesQuery.error?.message || practicesQuery.error?.message
        }
      />
    );

  const selectedNetwork = practicesQuery.data?.networks.find(
    (network) => network.network_id === selectedNetworkId
  );
  const packetPracticeCount =
    selectedNetwork?.practices.filter(isPacketPractice).length || 0;

  const familyCard = (family: 'SVA' | 'BNL') => {
    const rows = familyRows(family);
    const isGenerating =
      downloadPacketRequest.loading &&
      downloadPacketRequest.params?.[0] === family;
    const hasLetterTemplates = rows.some(
      (t) => t.template_type === 'letter' && t.letter_template
    );
    return (
      <Card
        title={`${family} Packet`}
        style={cardStyle}
        extra={
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            disabled={
              !hasLetterTemplates ||
              (downloadPacketRequest.loading && !isGenerating)
            }
            loading={isGenerating}
            onClick={() =>
              selectedNetworkId &&
              downloadPacketRequest.run(family, selectedNetworkId)
            }
          >
            {isGenerating && packetProgress?.family === family
              ? `Completed ${packetProgress.completed} of ${packetProgress.total} practices for ${family} previews`
              : selectedNetwork
              ? `Download ${family} Previews for ${packetPracticeCount} ${
                  packetPracticeCount === 1 ? 'practice' : 'practices'
                }`
              : `Select a Network to Download ${family} Previews`}
          </Button>
        }
      >
        {rows.length ? (
          <ApprovedTemplatesTable rows={rows} />
        ) : !selectedNetworkId ? (
          <Typography.Text type="secondary">
            Select a network to view its approved {family} templates.
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">
            No approved {family} templates have been assigned to this network.
            Please contact{' '}
            <a href="mailto:support@getmabel.com">support@getmabel.com</a>
          </Typography.Text>
        )}
      </Card>
    );
  };

  const otherRows = familyRows(null);

  return (
    <>
      <Helmet>
        <title>Approved Forms | Mabel</title>
      </Helmet>
      <div style={pageStyle}>
        <Typography.Title level={2} style={{ marginBottom: 4 }}>
          Approved Forms
        </Typography.Title>
        <Typography.Paragraph>
          These are the compliance-approved templates assigned to your networks.
          Downloads generate previews of every assigned language template for
          each of your practices and bundle them into a zip file, organized into
          practice folders. If you have a template that has been approved, but
          do not see it reflected in this list, contact{' '}
          <a href="mailto:support@getmabel.com">support@getmabel.com</a>
        </Typography.Paragraph>
        <div style={{ marginBottom: 24 }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Network
          </Typography.Text>
          <Select
            aria-label="Network"
            onChange={setSelectedNetworkId}
            optionFilterProp="label"
            options={(practicesQuery.data?.networks || [])
              .slice()
              .sort(compareClientNetworks)
              .map((network) => ({
                label: packetNetworkLabel(network),
                value: network.network_id,
              }))}
            placeholder="Select a network"
            showSearch
            style={{ maxWidth: '100%', width: 360 }}
            value={selectedNetworkId}
          />
        </div>
        {familyCard('SVA')}
        <Card
          title="SVA Provider Instructions"
          style={cardStyle}
          extra={
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              href="https://getmabel.com/assets/docs/Mabel-SVA-Provider-Packet.pdf"
              target="_blank"
            >
              Download SVA Provider Instructions
            </Button>
          }
        >
          <Typography.Text type="secondary">
            Download the SVA Provider Instructions and FAQ to share with your
            practices alongside their approved forms.
          </Typography.Text>
        </Card>
        {familyCard('BNL')}
        {otherRows.length ? (
          <Card title="Other Approved Templates" style={cardStyle}>
            <ApprovedTemplatesTable rows={otherRows} />
          </Card>
        ) : null}
      </div>
    </>
  );
};

export default ClientTemplatesPage;
